import React, { useEffect, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import CloseIcon from '@material-ui/icons/Close';
import I18n from '../../../components/shared/i18n/I18n.component';
import ButtonV2 from '../../../components/shared/Buttons/ButtonV2.container';
import colors from '../../../config/colors';
import getShadowStyle from '../../../config/shadow';

const FOOTER_CARD_HEIGHT = 64;
const FOOTER_BOTTOM_GAP = 16;
const FOOTER_TOP_GAP = 24;
/** Total vertical space the footer occupies - used by the page to shrink the
 * table so it doesn't run underneath the floating footer. */
export const SELECTION_FOOTER_SPACE = FOOTER_CARD_HEIGHT + FOOTER_BOTTOM_GAP + FOOTER_TOP_GAP;

/** Horizontal page padding - kept in sync with ReportsPageWrapper so the
 * floating card lines up with the table above it. */
const getSideInset = () =>
	typeof window !== 'undefined' && window.innerWidth <= 1600 ? 10 : 28;

type SelectionFooterProps = {
	count: number;
	continueDisabled?: boolean;
	continueLabel: string;
	onContinue: () => void;
	onCancel: () => void;
};

/**
 * Page-level floating action bar shown while the users table is in a bulk
 * selection mode. Shows how many rows are selected (with a close/X to exit)
 * and a single action button. Purely presentational - state lives in the parent.
 */
const SelectionFooter = ({
	count,
	continueDisabled,
	continueLabel,
	onContinue,
	onCancel,
}: SelectionFooterProps) => {
	const [inset, setInset] = useState(getSideInset());

	useEffect(() => {
		const onResize = () => setInset(getSideInset());
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	return (
		<View style={[styles.wrapper, { left: inset, right: inset }]}>
			<View style={styles.card}>
				<View style={styles.info}>
					<I18n
						size={15}
						font='medium'
						color='clockRecordBlue'
						count={count}
					>
						usersManagement.selectionFooter.count
					</I18n>
					<TouchableOpacity
						onPress={onCancel}
						style={styles.close}
						hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
					>
						<CloseIcon
							style={{ fontSize: 20, color: colors.greyText }}
						/>
					</TouchableOpacity>
				</View>
				<ButtonV2
					preset='outline'
					onPress={onContinue}
					disabled={continueDisabled}
				>
					<I18n color='titlePrimary' size={15} numberOfLines={1}>
						{continueLabel}
					</I18n>
				</ButtonV2>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	wrapper: {
		position: 'fixed' as any,
		bottom: FOOTER_BOTTOM_GAP,
		zIndex: 1000,
	},
	card: {
		height: FOOTER_CARD_HEIGHT,
		backgroundColor: colors.white,
		borderRadius: 8,
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingHorizontal: 16,
		paddingVertical: 12,
		boxShadow: '2px 2px 16px 0 rgba(0, 0, 0, 0.04)',
	},
	info: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	close: {
		marginLeft: 14,
	},
});

export default SelectionFooter;
